import { useState, useEffect, useCallback } from 'react'
import type { Cell, GfbundleVersion } from '../types'

async function getJson<T>(path: string, what: string): Promise<T> {
  const res = await fetch(path)
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null
    throw new Error(body?.error || `Failed to fetch ${what} (${res.status})`)
  }
  return res.json() as Promise<T>
}

/**
 * The gfbundle register and the cells, read from the routes gfbundle's CI and the cell
 * script write (GET /api/gfbundle/versions, GET /api/cells). Each list fails on its own,
 * so a refused register does not hide the cells.
 */
export function useGfbundles() {
  const [versions, setVersions] = useState<GfbundleVersion[]>([])
  const [cells, setCells] = useState<Cell[]>([])
  const [loading, setLoading] = useState(true)
  const [versionsError, setVersionsError] = useState<string | null>(null)
  const [cellsError, setCellsError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setVersionsError(null)
    setCellsError(null)
    const [v, c] = await Promise.allSettled([
      getJson<{ versions: GfbundleVersion[] }>('/api/gfbundle/versions', 'gfbundle versions'),
      getJson<{ cells: Cell[] }>('/api/cells', 'cells'),
    ])
    if (v.status === 'fulfilled') setVersions(v.value.versions || [])
    else setVersionsError(v.reason instanceof Error ? v.reason.message : 'Unknown error')
    if (c.status === 'fulfilled') setCells(c.value.cells || [])
    else setCellsError(c.reason instanceof Error ? c.reason.message : 'Unknown error')
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return { versions, cells, loading, versionsError, cellsError, refetch: fetchAll }
}
